/**
 * JSON-LD (schema.org) builders for SEO + GEO on the race microsite.
 *
 * Generative engines (ChatGPT, Perplexity, Google AI Overviews) lean heavily on
 * structured data to extract and cite facts. These builders emit plain objects
 * that `useDocumentMeta({ jsonLd })` serializes into <script type="application/ld+json">.
 */
import {
  SITE_URL,
  SITE_NAME,
  absoluteUrl,
  ORG_NAME,
  ORG_CONTACT_EMAIL,
  ORG_LOGO_URL,
  SITE_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  EVENT_START_ISO,
  EVENT_END_ISO,
  EVENT_VENUE_NAME,
  EVENT_STREET_ADDRESS,
  EVENT_LOCALITY,
  EVENT_POSTAL_CODE,
  EVENT_COUNTRY,
} from "./site";

type Json = Record<string, unknown>;

const ORG_ID = `${SITE_URL}/#organization`;
const WEBSITE_ID = `${SITE_URL}/#website`;
const EVENT_ID = `${SITE_URL}/#event`;

/** schema.org/Organization — the event brand (jointly presented by Bursa Malaysia & The Edge). */
export function organizationLd(): Json {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORG_ID,
    name: ORG_NAME,
    url: SITE_URL,
    logo: ORG_LOGO_URL,
    description: SITE_DESCRIPTION,
    email: ORG_CONTACT_EMAIL,
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "organizer",
        email: ORG_CONTACT_EMAIL,
        availableLanguage: ["English", "Malay"],
      },
    ],
  };
}

/** schema.org/WebSite — enables sitelinks / site identity for the domain. */
export function websiteLd(): Json {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    name: SITE_NAME,
    url: SITE_URL,
    publisher: { "@id": ORG_ID },
  };
}

/** schema.org/SportsEvent — the race itself (the key entity for GEO/event queries). */
export function sportsEventLd(): Json {
  return {
    "@context": "https://schema.org",
    "@type": "SportsEvent",
    "@id": EVENT_ID,
    name: SITE_NAME,
    description: SITE_DESCRIPTION,
    startDate: EVENT_START_ISO,
    endDate: EVENT_END_ISO,
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    image: [DEFAULT_OG_IMAGE],
    url: SITE_URL,
    sport: "Corporate relay running",
    location: {
      "@type": "Place",
      name: EVENT_VENUE_NAME,
      address: {
        "@type": "PostalAddress",
        streetAddress: EVENT_STREET_ADDRESS,
        addressLocality: EVENT_LOCALITY,
        postalCode: EVENT_POSTAL_CODE,
        addressCountry: EVENT_COUNTRY,
      },
    },
    organizer: [
      { "@type": "Organization", name: "Bursa Malaysia", url: "https://www.bursamalaysia.com" },
      { "@type": "Organization", name: "The Edge Malaysia", url: "https://theedgemalaysia.com" },
    ],
    offers: {
      "@type": "Offer",
      url: absoluteUrl("/registration"),
      availability: "https://schema.org/InStock",
      category: "Corporate team registration",
    },
  };
}

/**
 * schema.org/WebPage for a specific page. `breadcrumb` items are
 * `[{ name, path }]` ordered from Home → current page.
 */
export function webPageLd(opts: {
  name: string;
  description: string;
  path: string;
  breadcrumb?: Array<{ name: string; path: string }>;
}): Json {
  const page: Json = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: opts.name,
    description: opts.description,
    url: absoluteUrl(opts.path),
    isPartOf: { "@id": WEBSITE_ID },
    about: { "@id": ORG_ID },
  };
  if (opts.breadcrumb?.length) {
    page.breadcrumb = breadcrumbLd(opts.breadcrumb);
  }
  return page;
}

/** schema.org/BreadcrumbList from ordered `[{ name, path }]` items. */
export function breadcrumbLd(items: Array<{ name: string; path: string }>): Json {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}
